// src/encounter/use-non-prescriber-plan-draft.ts
// [PROD-2949] The page-side seam for both non-prescriber plans: the allied-health Plan step (closes
// MS-1, "encounter-shell-page never passes alliedPlan") and the nurse's Nursing plan block. One hook,
// one save path, two audiences.
//
//   - Reads the caller's own plan and shows it until the clinician edits.
//   - From the first edit the values are LOCAL (the server's echo never overwrites what the clinician
//     is typing) and are saved on a short debounce, one request at a time; an edit made during a save
//     is sent right after it. A pending edit is flushed when the page unmounts.
//   - The nurse's framework is fixed (nursing-process). Allied health picks one of the allied
//     frameworks when no speciality resolves; the pick is saved WITH the fields, in one request, as the
//     allied view's header asked for.
//   - Read-only when the page says so (a provider signed) or the server says the plan is locked.
//   - Refusals are shown in words: the server's own message (403 role, 409 signed), never swallowed.
//
// Does nothing at all while `enabled` is false: no read, no save, no state.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '@/shared/api/api-client';
import {
  fromRecord,
  isAlliedFramework,
  toSaveRequest,
  type PlanFrameworkKey,
} from '@/encounter/lib/non-prescriber-plan-record';
import type { AlliedHealthPlanViewProps } from './allied-health-plan-view';
import type { NursingPlanViewProps } from './nursing-plan-view';
import { NURSING_PLAN_FRAMEWORK } from '@/encounter/lib/nursing-plan';
import type { AlliedPlanFramework } from '@/encounter/lib/allied-health-plan';
import { useMyNonPrescriberPlan, useSaveMyNonPrescriberPlan } from '@/encounter/services/use-non-prescriber-plan';

export type PlanAudience = 'allied-health' | 'nurse';

export interface PlanSaveState {
  saving: boolean;
  /** True once the latest values are stored. */
  saved: boolean;
  /** A save that was tried and failed, or that the server refused — in words. Red "Not saved". */
  error?: string;
  /**
   * The app already knows this role cannot write the plan (`mayWrite === false`): nothing was tried,
   * so it is not a failure. An info "Read only" notice, never the red "Not saved" (design desk D-66).
   */
  readOnlyReason?: string;
  /** A neutral notice (e.g. choose a framework first). */
  status?: string;
}

export interface NonPrescriberPlanDraft {
  /** Switched on and the encounter is known: the page mounts the plan (read-only until `ready`). */
  active: boolean;
  /** The stored plan has been read and the values hydrated from it. */
  ready: boolean;
  framework: PlanFrameworkKey | null;
  values: Readonly<Record<string, string>>;
  readOnly: boolean;
  saveState: PlanSaveState;
  onChange: (fieldId: string, value: string) => void;
  /** Allied health only; ignored for the nurse. */
  onFrameworkChange: (framework: AlliedPlanFramework) => void;
}

export interface NonPrescriberPlanDraftOptions {
  patientId: string | null | undefined;
  encounterId: string | null | undefined;
  audience: PlanAudience;
  enabled: boolean;
  /** The page's own read-only signal (a provider signed the encounter). */
  readOnly?: boolean;
  /**
   * False when the app already knows this role cannot write a non-prescriber plan (a physician
   * persona: their plan is the A&P). The plan then shows read-only from the start with an info "Read
   * only" notice — never editable-then-refused, and never the red "Not saved" (design desk D-66).
   * Default true.
   */
  mayWrite?: boolean;
  debounceMs?: number;
}

export const CHOOSE_FRAMEWORK_STATUS = 'Choose the framework this plan follows to save it.';
export const LOADING_STATUS = 'Loading the plan…';
export const PENDING_STATUS = 'Changes not saved yet.';
export const ENCOUNTER_CHANGED_ERROR = 'Your last change to the plan was not saved because the encounter changed.';
export const ROLE_CANNOT_WRITE = 'Your role cannot write this plan, so it is shown read-only.';
const GENERIC_ERROR = 'The plan was not saved. Check your connection and try again.';

/**
 * The server's ROLE refusals (Scrips.Patient NonPrescriberPlanAuthorization). They are final for this
 * user, so the plan turns read-only. `plan-author-unconfirmed` is NOT one: the role could not be
 * confirmed right now, so the plan stays editable and the next edit tries again.
 */
const ROLE_REFUSAL_CODES: ReadonlySet<string> = new Set(['plan-not-for-prescribers', 'plan-framework-not-permitted']);

/** True for a 403 whose body carries one of the role refusal codes. */
export function isRoleRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 403) return false;
  try {
    const body = JSON.parse(error.body) as { code?: unknown };
    return typeof body.code === 'string' && ROLE_REFUSAL_CODES.has(body.code);
  } catch {
    return false;
  }
}

/** The server's own words for a refusal (`{ error }`), else a generic line. Never patient content. */
export function planSaveErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    try {
      const body = JSON.parse(error.body) as { error?: unknown };
      if (typeof body.error === 'string' && body.error.trim() !== '') return body.error;
    } catch {
      // not JSON: fall through
    }
  }
  return GENERIC_ERROR;
}

export function useNonPrescriberPlanDraft(options: NonPrescriberPlanDraftOptions): NonPrescriberPlanDraft {
  const { patientId, encounterId, audience, enabled, debounceMs = 800 } = options;
  const query = useMyNonPrescriberPlan(patientId, encounterId, enabled);
  const save = useSaveMyNonPrescriberPlan(patientId, encounterId);

  const fixedFramework: PlanFrameworkKey | null = audience === 'nurse' ? NURSING_PLAN_FRAMEWORK : null;
  const key = enabled && patientId && encounterId ? `${patientId}|${encounterId}` : null;
  const ready = !!key && query.isFetched && !query.isError;

  // The stored plan in the view's shape: the base until the clinician edits.
  const stored = useMemo(() => fromRecord(query.data), [query.data]);
  // The clinician's edits for THIS encounter. Once present they win over the stored plan, so the
  // server's echo of a save (or a background refetch) never overwrites what is being typed.
  const [edits, setEdits] = useState<Edits | null>(null);
  const mine = edits && edits.key === key ? edits : null;
  const values = mine?.values ?? stored.values;
  const framework = fixedFramework ?? (mine ? mine.framework : stored.framework);
  const [saveStatus, setSaveStatus] = useState<{ key: string | null; state: PlanSaveState } | null>(null);
  // A role refusal from the server, in its words. It is about the user, not the encounter, so it holds
  // for the rest of the page: the plan stays read-only rather than editable-then-refused again.
  const [serverRefusal, setServerRefusal] = useState<string | null>(null);
  const cannotWrite = options.mayWrite === false || serverRefusal !== null;

  // The latest values for the save, read when the debounce fires (never a render-time copy).
  const valuesRef = useRef(values);
  const frameworkRef = useRef(framework);
  const keyRef = useRef(key);
  const mutateRef = useRef(save.mutateAsync);
  useEffect(() => {
    valuesRef.current = values;
    frameworkRef.current = framework;
    keyRef.current = key;
    mutateRef.current = save.mutateAsync;
  }, [values, framework, key, save.mutateAsync]);
  const dirtyRef = useRef(false);
  const inFlightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The encounter the pending edits belong to: they are never sent under another one. */
  const pendingKeyRef = useRef<string | null>(null);

  /** Send the pending values, one request at a time; edits made during a save go in the next round. */
  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!dirtyRef.current || inFlightRef.current) return;
    const report = (state: PlanSaveState) => setSaveStatus({ key: keyRef.current, state });
    inFlightRef.current = true;
    try {
      while (dirtyRef.current) {
        // The page moved to another encounter: these edits were made for the old one, and the save
        // hook now points at the new one. Never write them to the wrong encounter, and say so.
        if (pendingKeyRef.current !== keyRef.current) {
          dirtyRef.current = false;
          report({ saving: false, saved: false, error: ENCOUNTER_CHANGED_ERROR });
          return;
        }
        const chosen = frameworkRef.current;
        if (!chosen) {
          report({ saving: false, saved: false, status: CHOOSE_FRAMEWORK_STATUS });
          return;
        }
        dirtyRef.current = false;
        report({ saving: true, saved: false });
        try {
          await mutateRef.current(toSaveRequest(chosen, valuesRef.current));
        } catch (error) {
          if (isRoleRefusal(error)) {
            // Final for this user: nothing to retry, and the fields turn read-only.
            setServerRefusal(planSaveErrorText(error));
            return;
          }
          // Keep the values; the next edit sends them again.
          dirtyRef.current = true;
          report({ saving: false, saved: false, error: planSaveErrorText(error) });
          return;
        }
      }
      report({ saving: false, saved: true });
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const schedule = useCallback(
    (forKey: string) => {
      dirtyRef.current = true;
      pendingKeyRef.current = forKey;
      // A refusal stays visible until the next edit; from then on the screen says the change is pending,
      // not that the old error still applies to it.
      setSaveStatus((prev) =>
        prev && prev.state.error ? { key: prev.key, state: { saving: false, saved: false, status: PENDING_STATUS } } : prev,
      );
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => void flush(), debounceMs);
    },
    [flush, debounceMs],
  );

  // The page owns this hook, so a step change keeps the debounce running; only leaving the encounter
  // (the page unmounting) needs the pending edit sent at once.
  useEffect(() => () => void flush(), [flush]);

  const locked = options.readOnly === true || query.data?.locked === true;
  const editable = ready && !locked && !cannotWrite;

  const onChange = useCallback(
    (fieldId: string, value: string) => {
      if (!editable || !key) return;
      setEdits((prev) => {
        const base = prev && prev.key === key ? prev : { key, values: stored.values, framework: stored.framework };
        return { ...base, values: { ...base.values, [fieldId]: value } };
      });
      schedule(key);
    },
    [editable, key, stored, schedule],
  );

  const onFrameworkChange = useCallback(
    (next: AlliedPlanFramework) => {
      if (!editable || !key || audience === 'nurse') return;
      setEdits((prev) => {
        const base = prev && prev.key === key ? prev : { key, values: stored.values, framework: stored.framework };
        return { ...base, framework: next };
      });
      schedule(key);
    },
    [editable, key, audience, stored, schedule],
  );

  const status = saveStatus && saveStatus.key === key ? saveStatus.state : { saving: false, saved: !!query.data };
  return {
    active: !!key,
    ready,
    framework,
    values,
    // Nothing is editable before the stored plan is read: typing first would be overwritten by it.
    readOnly: !editable,
    saveState: options.mayWrite === false
      ? { saving: false, saved: false, readOnlyReason: ROLE_CANNOT_WRITE }
      : serverRefusal !== null
      ? { saving: false, saved: false, error: serverRefusal }
      : query.isError
      ? { saving: false, saved: false, error: planSaveErrorText(query.error) }
      : ready
        ? status
        : { saving: false, saved: false, status: LOADING_STATUS },
    onChange,
    onFrameworkChange,
  };
}

interface Edits {
  key: string;
  values: Record<string, string>;
  framework: PlanFrameworkKey | null;
}

const NO_PLAN: NonPrescriberPlanDraft = {
  active: false,
  ready: false,
  framework: null,
  values: {},
  readOnly: true,
  saveState: { saving: false, saved: false },
  onChange: () => {},
  onFrameworkChange: () => {},
};

/**
 * The switched-OFF draft: inert, and it calls NO query hook. The page picks it once per mount when
 * `?ef=planSave` is off, so the OFF render never touches the plan's queries (nor needs a query
 * client) — today's page, exactly.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- same signature as the live hook
export function useNoNonPrescriberPlanDraft(_options: NonPrescriberPlanDraftOptions): NonPrescriberPlanDraft {
  return NO_PLAN;
}

/** The allied-health Plan step's props from the draft (the page's `alliedPlan`). */
export function alliedPlanProps(draft: NonPrescriberPlanDraft): AlliedHealthPlanViewProps {
  return {
    values: draft.values,
    onChange: draft.onChange,
    readOnly: draft.readOnly,
    framework: isAlliedFramework(draft.framework) ? draft.framework : null,
    onFrameworkChange: draft.onFrameworkChange,
    saveState: draft.saveState,
  };
}

/** The nurse's Nursing plan props from the draft (the page's `nursingPlan`). */
export function nursingPlanProps(draft: NonPrescriberPlanDraft): NursingPlanViewProps {
  return { values: draft.values, onChange: draft.onChange, readOnly: draft.readOnly, saveState: draft.saveState };
}
